import React, { useEffect } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { BlockPicker } from 'react-color';
import { useHistory } from 'react-router-dom';

import { PageLayout } from '@/components/templates/PageLayout';
import {
  AddIcon,
  CmBox,
  CmButton,
  CmCard,
  CmCheckBox,
  CmErrorText,
  CmFlex,
  CmFormErrorMessage,
  CmFormLabel,
  CmHeading,
  CmHelpPopover,
  CmHelpterText,
  CmInput,
  CmLabelAndInput,
  CmLabelAndPasswordInput,
  CmLabelAndSelect,
  CmLabelAndSingleReactSelect,
  CmLabelAndTextarea,
  CmLabelAndUpload,
  CmLongLoading,
  CmMessageCard,
  CmRadio,
  CmRadioGroup,
  CmScrollBox,
  CmSelect,
  CmSelectDigitsTime,
  CmStack,
  CmText,
  DragHandleIcon,
} from '@/components/uiParts';
import { CmTag } from '@/components/uiParts/Tag/CmTag';
import { unitChangeForByte } from '@/domain/models/Image';
import {
  existsBySettingKey,
  existsInPayTypeSettings,
  gmoSettingRequired,
  gmoShopInformationRequired,
  payTypeTitle,
  SitePayTypeSettingKey,
  sitePayTypeSettingKeyTitle,
  threeDSecureSettingRequired,
} from '@/domain/models/Order';
import { OrderMethod, orderMethods } from '@/domain/models/Order/orderMethod';
import { isAvailableFeature, redirect404Page } from '@/lib/AccessControl';
import { OrderMethodDisplayNameSettingFormItem } from '@/pages/camel-order/sites/components';
import { CustomFieldForm } from '@/pages/camel-order/sites/components/CustomFieldForm';
import { SiteStatusRadioGroup } from '@/pages/camel-order/sites/components/SiteStatusRadioGroup';
import { useCamelOrderSiteForm } from '@/pages/camel-order/sites/hooks/useCamelOrderSiteForm';
import {
  deliveryCartItemLimitOptions,
  IMAGE_RULE,
  isAvailablePayType,
  orderMethodAndSitePayTypeSettingKeysCombinations,
  shouldShowGMOSettingPendingMessage,
  siteForm,
  sitePayTypeMapByPayTypeSettingKey,
  siteStatusMap,
} from '@/pages/camel-order/sites/service';
import { validateLinkUrl } from '@/pages/camel-order/sites/validate';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';

import Image01 from './images/image01.png';
import Image02 from './images/image02.png';

const CamelOrderSitesPage: React.FC = () => {
  // /* アクセス制御 */
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  redirect404Page('camel_order_site_setting', history, managerAccountMe);

  const {
    isLoading,
    sites,
    initialSites,
    siteIndex,
    error,
    handleChangeSiteUrl,
    handleChangeStatus,
    handleInputPassword,
    isShowPassword,
    togglePasswordVisibility,
    handleChangePayTypeSetting,
    handleChangeOrderMethod,
    handleChangeTaxRoundType,
    handleChangeLogoImage,
    handleChangeFaviconImage,
    handleClearImage,
    handleChangeColor,
    handleInputName,
    handleInputNameKana,
    handleInputNameAlphabet,
    handleInputNameShort,
    handleInputContactName,
    handleInputContactEmail,
    handleInputContactPhone,
    handleChangeContactOpeningHour,
    handleChangeContactOpeningMinute,
    handleChangeContactClosingHour,
    handleChangeContactClosingMinute,
    handleInputFaQUrl,
    handleInputInquiryUrl,
    handleInputTermUrl,
    handleInputSctlUrl,
    handleInputPrivacyUrl,
    handleInputTitle,
    handleInputDescription,
    handleInputGoogleTagId,
    handleAddGoogleTagId,
    handleRemoveGoogleTagId,
    handleRemoveCustomField,
    isOrderMethodChecked,
    handleChangeCustomField,
    handleChangeCustomFieldOrderMethod,
    handleChangeBannerImage,
    handleDeleteBannerImage,
    handleAddSiteBannerImage,
    handleOnDragEnd,
    handleUpdateSite,
    handleChangedisplaySpecialInstruction,
    handleChangeIsAuthAvailable,
    handleInputNotificationText,
    initializeSitePage,
    handleChangeBanneerLinkUrl,
    handleChangeIsCustomOrderTypeDisplayName,
    handleChangeOrderTypeDisplayName,
    handleAddCustomField,
    handleChangeDeliveryMinimumAmount,
    handleChangeDeliveryCartItemLimit,
    handleChangeUseThreeDSecure2,
  } = useCamelOrderSiteForm();

  const siteUrlOptions = sites.map((site) => site.url);

  const convertToDateTime = (
    timeString: string,
    dateString = '1970-01-01'
  ): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    const [hours, minutes] = timeString.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes, 0, 0);
  };

  const isDisabledUpdateButton = (() => {
    if (sites.length === 0) return true;
    const currentSite = sites[siteIndex];
    const errorConditions = [
      // 本来は空文字判定を入れないようにしたいが、必須箇所が最初に空文字で来るため入れている。
      currentSite.logo.image_url === '',
      sites[siteIndex].term_url === '' || error.title.isError,
      currentSite.sctl_url === '' || error.sctlUrl.isError,
      error.faqUrl.isError,
      error.inquiryUrl.isError,
      currentSite.privacy_url === '' || error.privacyUrl.isError,
      currentSite.term_url === '' || error.termUrl.isError,
      currentSite.title === '' || error.title.isError,
      currentSite.description === '' || error.description.isError,
      currentSite.site_banners.length === 0,
      currentSite.site_banners.some((banner) => banner.image_url === ''),
      currentSite.status === 2 && currentSite.password === '', // statusが限定公開の場合、パスワードが必須
      currentSite.order_methods.length === 0,
      currentSite.custom_fields.some(
        (customField) =>
          customField.name === '' ||
          customField.description === '' ||
          (!customField.delivery && !customField.eat_in && !customField.takeout)
      ),
      error.customFieldName.isError,
      error.customFieldDescription.isError,
      error.customFieldMaximum.isError,
      error.customFieldMinimum.isError,
      error.customFieldRegex.isError,
      error.notificationText.isError,
      error.orderTypeDisplayNameByDeliveryText.isError,
      error.orderTypeDisplayNameByTakeoutText.isError,
      error.orderTypeDisplayNameByEatInText.isError,
      error.orderTypeDisplayNameByDeliveryButtonText.isError,
      error.orderTypeDisplayNameByTakeoutButtonText.isError,
      error.orderTypeDisplayNameByEatInButtonText.isError,
    ];
    const customOrderMethodText = sites[siteIndex].custom_order_method_text;
    if (customOrderMethodText.delivery.is_custom === '1') {
      errorConditions.push(
        customOrderMethodText.delivery.text === '' ||
          customOrderMethodText.delivery.button_text === ''
      );
    }
    if (customOrderMethodText.eat_in.is_custom === '1') {
      errorConditions.push(
        customOrderMethodText.eat_in.text === '' ||
          customOrderMethodText.eat_in.button_text === ''
      );
    }
    if (customOrderMethodText.takeout.is_custom === '1') {
      errorConditions.push(
        customOrderMethodText.takeout.text === '' ||
          customOrderMethodText.takeout.button_text === ''
      );
    }
    if (currentSite.order_methods.includes('TAKE_OUT')) {
      errorConditions.push(
        !existsBySettingKey(currentSite.pay_type_settings, 'TAKE_OUT')
      );
    }
    if (currentSite.order_methods.includes('EAT_IN')) {
      errorConditions.push(
        !existsBySettingKey(currentSite.pay_type_settings, 'EAT_IN')
      );
    }
    if (currentSite.order_methods.includes('DELIVERY')) {
      errorConditions.push(
        !(
          existsBySettingKey(currentSite.pay_type_settings, 'DELIVERY_UBER') ||
          existsBySettingKey(currentSite.pay_type_settings, 'DELIVERY_IN_HOUSE')
        )
      );
    }

    if (gmoShopInformationRequired(currentSite.pay_type_settings)) {
      const gmoShopErrorConditions = [
        currentSite.gmo_shop.name === '' || error.gmoShopName.isError,
        currentSite.gmo_shop.name_kana === '' || error.gmoShopNameKana.isError,
        currentSite.gmo_shop.name_alphabet === '' ||
          error.gmoShopNameAlphabet.isError,
        currentSite.gmo_shop.contact_name === '' ||
          error.gmoShopContactName.isError,
        currentSite.gmo_shop.contact_email === '' ||
          error.gmoShopContactEmail.isError,
        currentSite.gmo_shop.contact_phone === '' ||
          error.gmoShopContactPhone.isError,
        (currentSite.gmo_shop.contact_opening_hours === '00:00' &&
          currentSite.gmo_shop.contact_closing_hours === '00:00') ||
          error.gmoShopContactOpeningHours.isError,
      ];
      errorConditions.push(...gmoShopErrorConditions);
    }
    return errorConditions.some((error) => error);
  })();

  useEffect(() => {
    (async () => {
      initializeSitePage();
    })();
  }, []);

  // FIXME: バックエンドの構造を無視して使用可能な決済手段を絞り込んでしまっている
  const paymentMethodConfig = sites[siteIndex]?.gmo_shop_payment_method_config;

  // 店内飲食のアップセルのため利用可否を判定
  const isAvailableEatin = isAvailableFeature(
    'camel_order_eatin',
    managerAccountMe
  );

  return (
    <PageLayout title={routes.CamelOrderSites.title}>
      <div>
        {sites.length > 0 && initialSites.length > 0 ? (
          <CmCard
            mt="25px"
            height={`calc(100vh - 120px)`}
            padding={'20px 40px 10px'}
          >
            <CmFlex justify="space-between">
              <CmFlex alignItems="center" p="0 0 10px">
                <CmLabelAndSelect
                  id={siteForm.siteUrl.key}
                  label={siteForm.siteUrl.label}
                  width="300px"
                  fontWeight="bold"
                  value={siteIndex}
                  onChange={handleChangeSiteUrl}
                >
                  {siteUrlOptions.map((siteUrlOption, index) => (
                    <option key={siteUrlOption} value={index}>
                      {siteUrlOption}
                    </option>
                  ))}
                </CmLabelAndSelect>
                <CmTag
                  ml="20px"
                  mt="25px"
                  colorScheme={
                    siteStatusMap[initialSites[siteIndex].status].color
                  }
                >
                  {siteStatusMap[initialSites[siteIndex].status].name}
                </CmTag>
              </CmFlex>
              {/*
               // @ts-ignore TS2590 */}
              <CmButton
                isDisabled={isDisabledUpdateButton}
                onClick={handleUpdateSite}
              >
                更新
              </CmButton>
            </CmFlex>
            <CmScrollBox>
              <CmBox mt="20px">
                <CmHeading fontSize="18px" pb="10px" isRequired>
                  {siteForm.status.label}
                </CmHeading>
                <CmBox pl="20px">
                  <SiteStatusRadioGroup
                    status={sites[siteIndex].status}
                    onChange={handleChangeStatus}
                  />
                  <CmBox
                    mt="5px"
                    display={sites[siteIndex].status === 2 ? 'block' : 'none'}
                  >
                    <CmLabelAndPasswordInput
                      isRequired
                      label={'パスワード'}
                      id={siteForm.password.key}
                      fontWeight="bold"
                      isShow={isShowPassword}
                      handleShowPassword={togglePasswordVisibility}
                      isInvalid={error[siteForm.password.key].isError}
                      {...{
                        type: isShowPassword ? 'text' : 'password',
                        value: sites[siteIndex].password,
                        onChange: handleInputPassword,
                      }}
                    >
                      <CmFormErrorMessage>
                        {error[siteForm.password.key].message}
                      </CmFormErrorMessage>
                    </CmLabelAndPasswordInput>
                  </CmBox>
                </CmBox>
              </CmBox>

              <CmBox mt="20px">
                <CmHeading fontSize="18px" pb="10px" isRequired>
                  対応注文方式
                </CmHeading>
                <CmBox pl="20px">
                  <CmHelpterText mb="5px">
                    ※最低一つを選択して下さい。
                  </CmHelpterText>
                  <CmStack spacing="15px">
                    {orderMethods.map((option, index) => (
                      <CmCheckBox
                        key={index}
                        ml={index > 0 ? '20px' : undefined}
                        isChecked={sites[siteIndex].order_methods.includes(
                          option.value
                        )}
                        value={option.value}
                        onChange={handleChangeOrderMethod}
                        isDisabled={
                          option.value === 'EAT_IN' && !isAvailableEatin
                        }
                      >
                        {option.name}
                      </CmCheckBox>
                    ))}
                  </CmStack>
                </CmBox>
              </CmBox>

              {orderMethodAndSitePayTypeSettingKeysCombinations.map(
                (
                  [orderMethodStr, sitePayTypeSettingKeys],
                  orderMethodIndex
                ) => {
                  const orderMethod = orderMethodStr as OrderMethod;

                  return (
                    sites[siteIndex].order_methods.includes(orderMethod) &&
                    sitePayTypeSettingKeys.map(
                      (sitePayTypeSettingKeyStr, payTypeSettingIndex) => {
                        const payTypeSettingKey =
                          sitePayTypeSettingKeyStr as SitePayTypeSettingKey;
                        const payTypeSettingKeyTitle =
                          sitePayTypeSettingKeyTitle(payTypeSettingKey);
                        const payTypeMap =
                          sitePayTypeMapByPayTypeSettingKey(payTypeSettingKey);

                        return (
                          <CmBox
                            mt="20px"
                            key={`${orderMethodIndex}-${payTypeSettingIndex}`}
                          >
                            <CmHeading fontSize="18px" pb="10px">
                              {payTypeTitle(orderMethod)}
                            </CmHeading>
                            {payTypeSettingKeyTitle !== '' && (
                              <CmBox pl="20px">
                                <CmHeading fontSize="16px" pb="10px">
                                  {payTypeSettingKeyTitle}
                                </CmHeading>
                              </CmBox>
                            )}
                            <CmBox pl="40px">
                              <CmHelpterText mb="5px">
                                ※最低一つを選択して下さい。
                              </CmHelpterText>
                              <CmStack spacing="15px">
                                {payTypeMap
                                  .filter((pm) =>
                                    isAvailablePayType(
                                      pm.value,
                                      paymentMethodConfig
                                    )
                                  )
                                  .map((option, optionIndex) => (
                                    <React.Fragment
                                      key={`option-${orderMethodIndex}-${payTypeSettingIndex}-${optionIndex}`}
                                    >
                                      {shouldShowGMOSettingPendingMessage(
                                        option.value,
                                        paymentMethodConfig,
                                        sites[siteIndex].gmo_setting_completed
                                      ) && (
                                        <CmHelpterText mt="5px">
                                          ※以下は決済代行会社による審査中です。審査完了後に選択可能になります。
                                        </CmHelpterText>
                                      )}
                                      <CmCheckBox
                                        ml={
                                          optionIndex > 0 ? '20px' : undefined
                                        }
                                        isChecked={existsInPayTypeSettings(
                                          sites[siteIndex].pay_type_settings,
                                          payTypeSettingKey,
                                          option.value
                                        )}
                                        isDisabled={
                                          gmoSettingRequired(option.value) &&
                                          !sites[siteIndex]
                                            .gmo_setting_completed
                                        }
                                        value={option.value}
                                        onChange={(e) =>
                                          handleChangePayTypeSetting(
                                            payTypeMap,
                                            payTypeSettingKey,
                                            e
                                          )
                                        }
                                      >
                                        {option.name}
                                      </CmCheckBox>
                                    </React.Fragment>
                                  ))}
                              </CmStack>
                            </CmBox>
                          </CmBox>
                        );
                      }
                    )
                  );
                }
              )}

              {threeDSecureSettingRequired(
                sites[siteIndex].pay_type_settings
              ) &&
                sites[siteIndex].gmo_shop &&
                sites[siteIndex].is_three_d_secure2_setting_available && (
                  <CmBox mt="20px">
                    <CmHeading fontSize="18px" pb="10px">
                      クレジットカード 3Dセキュア設定
                    </CmHeading>
                    <CmBox pl="20px">
                      <CmStack spacing="15px">
                        <CmBox mt="5px">
                          <CmCheckBox
                            isChecked={sites[siteIndex].use_three_d_secure2}
                            onChange={handleChangeUseThreeDSecure2}
                          >
                            3Dセキュアを利用する
                          </CmCheckBox>
                        </CmBox>
                      </CmStack>
                    </CmBox>
                  </CmBox>
                )}

              {sites[siteIndex].order_methods.includes('DELIVERY') && (
                <CmBox mt="20px">
                  <CmHeading fontSize="18px" pb="10px">
                    カートに追加できる商品数上限（デリバリー）
                  </CmHeading>
                  <CmBox pl="20px">
                    <CmSelect
                      width="8em"
                      value={
                        sites[siteIndex].delivery_cart_item_limit?.toString() ??
                        ''
                      }
                      onChange={handleChangeDeliveryCartItemLimit}
                    >
                      <option value="" selected>
                        設定なし
                      </option>
                      {deliveryCartItemLimitOptions.map((num) => (
                        <option key={num} value={num}>
                          {num}点
                        </option>
                      ))}
                    </CmSelect>
                  </CmBox>
                </CmBox>
              )}

              {/* TODO: 最低注文金額のOrderの開発が終了したらコメントアウトを外す */}
              {sites[siteIndex].order_methods.includes('DELIVERY') && (
                <CmBox mt="20px">
                  <CmHeading fontSize="18px" pb="10px">
                    最低注文金額（デリバリー）
                  </CmHeading>
                  <CmBox pl="20px">
                    <CmInput
                      id={siteForm.deliveryMinimumAmount.key}
                      value={sites[siteIndex].delivery_minimum_amount}
                      onChange={handleChangeDeliveryMinimumAmount}
                      isInvalid={
                        error[siteForm.deliveryMinimumAmount.key].isError
                      }
                    />
                  </CmBox>
                </CmBox>
              )}
              {gmoShopInformationRequired(sites[siteIndex].pay_type_settings) &&
                sites[siteIndex].gmo_shop && (
                  <CmBox mt="20px">
                    <CmHeading fontSize="18px" pb="10px">
                      GMO-PG加盟店情報
                    </CmHeading>
                    <CmBox pl="20px">
                      <CmStack spacing="15px">
                        <CmBox mt="5px">
                          <CmLabelAndInput
                            isRequired
                            label={siteForm.gmoShopName.label}
                            id={siteForm.gmoShopName.key}
                            fontWeight="bold"
                            value={sites[siteIndex].gmo_shop.name}
                            onChange={handleInputName}
                            isInvalid={error[siteForm.gmoShopName.key].isError}
                          >
                            <CmFormErrorMessage>
                              {error[siteForm.gmoShopName.key].message}
                            </CmFormErrorMessage>
                          </CmLabelAndInput>
                          <CmHelpterText mt="3px">全角15字まで</CmHelpterText>
                        </CmBox>
                        <CmBox mt="5px">
                          <CmLabelAndInput
                            isRequired
                            label={siteForm.gmoShopNameKana.label}
                            id={siteForm.gmoShopNameKana.key}
                            fontWeight="bold"
                            value={sites[siteIndex].gmo_shop.name_kana}
                            onChange={handleInputNameKana}
                            isInvalid={
                              error[siteForm.gmoShopNameKana.key].isError
                            }
                          >
                            <CmFormErrorMessage>
                              {error[siteForm.gmoShopNameKana.key].message}
                            </CmFormErrorMessage>
                          </CmLabelAndInput>
                          <CmHelpterText mt="3px">全角20字まで</CmHelpterText>
                        </CmBox>
                        <CmBox mt="5px">
                          <CmLabelAndInput
                            isRequired
                            label={siteForm.gmoShopNameAlphabet.label}
                            id={siteForm.gmoShopNameAlphabet.key}
                            fontWeight="bold"
                            value={sites[siteIndex].gmo_shop.name_alphabet}
                            onChange={handleInputNameAlphabet}
                            isInvalid={
                              error[siteForm.gmoShopNameAlphabet.key].isError
                            }
                          >
                            <CmFormErrorMessage>
                              {error[siteForm.gmoShopNameAlphabet.key].message}
                            </CmFormErrorMessage>
                          </CmLabelAndInput>
                          <CmHelpterText mt="3px">半角25字まで</CmHelpterText>
                        </CmBox>
                        <CmBox mt="5px">
                          <CmLabelAndInput
                            isRequired
                            label={siteForm.gmoShopNameShort.label}
                            id={siteForm.gmoShopNameShort.key}
                            fontWeight="bold"
                            value={sites[siteIndex].gmo_shop.name_short}
                            onChange={handleInputNameShort}
                            isInvalid={
                              error[siteForm.gmoShopNameShort.key].isError
                            }
                          >
                            <CmFormErrorMessage>
                              {error[siteForm.gmoShopNameShort.key].message}
                            </CmFormErrorMessage>
                          </CmLabelAndInput>
                          <CmHelpterText mt="3px">全角15字まで</CmHelpterText>
                        </CmBox>

                        <CmBox mt="5px">
                          <CmLabelAndInput
                            isRequired
                            label={siteForm.gmoShopContactName.label}
                            id={siteForm.gmoShopContactName.key}
                            fontWeight="bold"
                            value={sites[siteIndex].gmo_shop.contact_name}
                            onChange={handleInputContactName}
                            isInvalid={
                              error[siteForm.gmoShopContactName.key].isError
                            }
                          >
                            <CmFormErrorMessage>
                              {error[siteForm.gmoShopContactName.key].message}
                            </CmFormErrorMessage>
                          </CmLabelAndInput>
                          <CmHelpterText mt="3px">全角21字まで</CmHelpterText>
                        </CmBox>

                        <CmBox mt="5px">
                          <CmLabelAndInput
                            isRequired
                            label={siteForm.gmoShopContactEmail.label}
                            id={siteForm.gmoShopContactEmail.key}
                            fontWeight="bold"
                            value={sites[siteIndex].gmo_shop.contact_email}
                            onChange={handleInputContactEmail}
                            isInvalid={
                              error[siteForm.gmoShopContactEmail.key].isError
                            }
                          >
                            <CmFormErrorMessage>
                              {error[siteForm.gmoShopContactEmail.key].message}
                            </CmFormErrorMessage>
                          </CmLabelAndInput>
                        </CmBox>

                        <CmBox mt="5px">
                          <CmLabelAndInput
                            isRequired
                            label={siteForm.gmoShopContactPhone.label}
                            id={siteForm.gmoShopContactPhone.key}
                            fontWeight="bold"
                            value={sites[siteIndex].gmo_shop.contact_phone}
                            onChange={handleInputContactPhone}
                            isInvalid={
                              error[siteForm.gmoShopContactPhone.key].isError
                            }
                          >
                            <CmFormErrorMessage>
                              {error[siteForm.gmoShopContactPhone.key].message}
                            </CmFormErrorMessage>
                          </CmLabelAndInput>
                          <CmHelpterText mt="3px">
                            ハイフン（-）なしで入力
                          </CmHelpterText>
                        </CmBox>

                        <CmBox width="full">
                          <CmFormLabel fontWeight="bold" isRequired>
                            {siteForm.gmoShopContactOpeningHours.label}
                          </CmFormLabel>
                          <CmFlex alignItems="center" width="full">
                            <CmSelectDigitsTime
                              date={convertToDateTime(
                                sites[siteIndex].gmo_shop.contact_opening_hours
                              )}
                              handleChangeHour={handleChangeContactOpeningHour}
                              handleChangeMinutes={
                                handleChangeContactOpeningMinute
                              }
                            />
                            <CmText textAlign="center" mx="20px">
                              〜
                            </CmText>
                            <CmSelectDigitsTime
                              date={convertToDateTime(
                                sites[siteIndex].gmo_shop.contact_closing_hours
                              )}
                              handleChangeHour={handleChangeContactClosingHour}
                              handleChangeMinutes={
                                handleChangeContactClosingMinute
                              }
                            />
                          </CmFlex>
                          {error[siteForm.gmoShopContactOpeningHours.key]
                            .isError && (
                            <CmBox mb={8}>
                              <CmErrorText>
                                {
                                  error[siteForm.gmoShopContactOpeningHours.key]
                                    .message
                                }
                              </CmErrorText>
                            </CmBox>
                          )}
                        </CmBox>
                      </CmStack>
                    </CmBox>
                  </CmBox>
                )}

              <CmBox mt="20px">
                <CmHeading fontSize="18px" pb="10px" isRequired>
                  {siteForm.tax.label}
                </CmHeading>
                <CmFlex pl="10px">
                  <CmLabelAndSingleReactSelect
                    label=""
                    name="taxRoundType"
                    fontWeight="bold"
                    closeMenuOnSelect
                    isClearable={false}
                    singleSelectInput={handleChangeTaxRoundType(
                      sites[siteIndex].tax_round_type
                    )}
                  />
                </CmFlex>
              </CmBox>

              <CmBox mt="20px">
                <CmHeading fontSize="18px" pb="10px">
                  サイト画像設定
                </CmHeading>
                <CmFlex pl="20px">
                  <CmLabelAndUpload
                    id={siteForm.logoUrl.key}
                    label={siteForm.logoUrl.label}
                    isRequired
                    fontWeight="bold"
                    src={sites[siteIndex].logo.image_url}
                    accept={IMAGE_RULE.LOGO.allowedImageTypes.join(',')}
                    onChange={(e) => {
                      const file = (e.target.files as FileList)[0];
                      handleChangeLogoImage(file);
                    }}
                    onClear={() => handleClearImage('logo')}
                  >
                    <CmHelpterText mt="3px">
                      ファイルの要件:
                      {IMAGE_RULE.LOGO.allowedImageFormats.join('、')} (最大
                      {unitChangeForByte(IMAGE_RULE.LOGO.maxSize)})
                      <br />
                    </CmHelpterText>
                  </CmLabelAndUpload>
                  <CmHelpPopover>
                    <CmText mb="10px">
                      注文画面の上部に設定されるロゴになります。
                    </CmText>
                    <img src={Image01} alt="camel-order" />
                  </CmHelpPopover>
                </CmFlex>

                <CmBox mt="20px" pl="20px">
                  <CmLabelAndUpload
                    id={siteForm.faviconUrl.key}
                    label={siteForm.faviconUrl.label}
                    fontWeight="bold"
                    src={sites[siteIndex].favicon.image_url}
                    accept={'image/png'}
                    onChange={(e) => {
                      const file = (e.target.files as FileList)[0];
                      handleChangeFaviconImage(file);
                    }}
                    onClear={() => handleClearImage('favicon')}
                  >
                    <CmHelpterText mt="3px">
                      画像サイズ比率：1:1 (180px*180px以上推奨)
                      <br />
                      ファイルの要件:PNG (最大
                      {unitChangeForByte(IMAGE_RULE.FAVICON.maxSize)})
                      <br />
                    </CmHelpterText>
                  </CmLabelAndUpload>
                </CmBox>
              </CmBox>

              <CmBox mt="20px">
                <CmHeading fontSize="18px" pb="10px">
                  サイトカラー設定
                </CmHeading>
                <CmFlex pl="20px" justify="space-between" mt="10px">
                  <CmBox>
                    <CmFormLabel isRequired>
                      {siteForm.primaryColor.label}
                    </CmFormLabel>
                    <BlockPicker
                      colors={[]}
                      color={sites[siteIndex].primary_color}
                      onChange={handleChangeColor}
                    />
                  </CmBox>

                  <CmHelpPopover>
                    <CmText mb="10px">
                      サイトのテーマカラーとなり、ボタンやフォントに適用されます。
                    </CmText>
                    <img src={Image02} alt="camel-order" />
                  </CmHelpPopover>
                </CmFlex>
              </CmBox>

              <CmBox mt="20px">
                <CmHeading fontSize="18px" pb="10px">
                  各種規約・問い合わせ設定
                </CmHeading>
                <CmBox pl="20px">
                  <CmStack spacing="15px">
                    <CmLabelAndInput
                      fontWeight="bold"
                      label={siteForm.faqUrl.label}
                      id={siteForm.faqUrl.key}
                      value={sites[siteIndex].faq_url}
                      isInvalid={error[siteForm.faqUrl.key].isError}
                      onChange={handleInputFaQUrl}
                      placeholder={'https://www.google.com/faq'}
                    >
                      <CmFormErrorMessage>
                        {error[siteForm.faqUrl.key].message}
                      </CmFormErrorMessage>
                    </CmLabelAndInput>
                    <CmLabelAndInput
                      fontWeight="bold"
                      label={siteForm.inquiryUrl.label}
                      id={siteForm.inquiryUrl.key}
                      isInvalid={error[siteForm.inquiryUrl.key].isError}
                      value={sites[siteIndex].inquiry_url}
                      onChange={handleInputInquiryUrl}
                      placeholder={'https://www.google.com/inquiry'}
                    >
                      <CmFormErrorMessage>
                        {error[siteForm.inquiryUrl.key].message}
                      </CmFormErrorMessage>
                    </CmLabelAndInput>
                    <CmLabelAndInput
                      fontWeight="bold"
                      isRequired
                      label={siteForm.termUrl.label}
                      id={siteForm.termUrl.key}
                      value={sites[siteIndex].term_url}
                      onChange={handleInputTermUrl}
                      isInvalid={error[siteForm.termUrl.key].isError}
                      placeholder={'https://www.google.com/term'}
                    >
                      <CmFormErrorMessage>
                        {error[siteForm.termUrl.key].message}
                      </CmFormErrorMessage>
                    </CmLabelAndInput>
                    <CmLabelAndInput
                      fontWeight="bold"
                      isRequired
                      label={siteForm.sctlUrl.label}
                      id={siteForm.sctlUrl.key}
                      value={sites[siteIndex].sctl_url}
                      onChange={handleInputSctlUrl}
                      isInvalid={error[siteForm.sctlUrl.key].isError}
                      placeholder={
                        'https://www.google.com/specified-commercial-transactions'
                      }
                    >
                      <CmFormErrorMessage>
                        {error[siteForm.sctlUrl.key].message}
                      </CmFormErrorMessage>
                    </CmLabelAndInput>
                    <CmLabelAndInput
                      fontWeight="bold"
                      isRequired
                      label={siteForm.privacyUrl.label}
                      id={siteForm.privacyUrl.key}
                      value={sites[siteIndex].privacy_url}
                      onChange={handleInputPrivacyUrl}
                      isInvalid={error[siteForm.privacyUrl.key].isError}
                      placeholder={'https://www.google.com/privacy'}
                    >
                      <CmFormErrorMessage>
                        {error[siteForm.privacyUrl.key].message}
                      </CmFormErrorMessage>
                    </CmLabelAndInput>
                  </CmStack>
                </CmBox>
              </CmBox>

              <CmBox mt="20px">
                <CmHeading fontSize="18px" pb="10px">
                  SEO設定
                </CmHeading>
                <CmBox pl="20px">
                  <CmStack spacing="15px">
                    <CmLabelAndInput
                      isRequired
                      fontWeight="bold"
                      label={siteForm.title.label}
                      id={siteForm.title.key}
                      value={sites[siteIndex].title}
                      onChange={handleInputTitle}
                      isInvalid={error[siteForm.title.key].isError}
                    >
                      <CmFormErrorMessage>
                        {error[siteForm.title.key].message}
                      </CmFormErrorMessage>
                    </CmLabelAndInput>
                    <CmLabelAndTextarea
                      isRequired
                      fontWeight="bold"
                      label={siteForm.description.label}
                      id={siteForm.description.key}
                      value={sites[siteIndex].description}
                      onChange={handleInputDescription}
                      isInvalid={error[siteForm.description.key].isError}
                      error={
                        <CmFormErrorMessage>
                          {error[siteForm.description.key].message}
                        </CmFormErrorMessage>
                      }
                    />
                  </CmStack>
                </CmBox>
              </CmBox>

              <CmBox mt="20px">
                <CmHeading fontSize="18px" pb="10px">
                  Google Analytics コード（Google タグ）埋め込み
                </CmHeading>
                <CmBox pl="20px" mt="10px">
                  <CmFormLabel fontWeight="bold">測定ID</CmFormLabel>
                  <CmStack spacing="15px">
                    {sites[siteIndex].google_tag_ids.map(
                      (googleTagId, googleTagIdIndex) => (
                        <CmFlex
                          key={googleTagIdIndex}
                          maxWidth="300px"
                          alignItems="end"
                        >
                          <CmInput
                            id={siteForm.googleTagId.key}
                            value={googleTagId}
                            placeholder="G-ABCDE9FG83"
                            onChange={(e) => {
                              handleInputGoogleTagId(e, googleTagIdIndex);
                            }}
                          />
                          <CmButton
                            ml="15px"
                            colorScheme="red"
                            onClick={() => {
                              handleRemoveGoogleTagId(googleTagIdIndex);
                            }}
                          >
                            削除
                          </CmButton>
                        </CmFlex>
                      )
                    )}
                  </CmStack>
                  <CmButton
                    mt="15px"
                    leftIcon={<AddIcon />}
                    variant="outline"
                    onClick={handleAddGoogleTagId}
                  >
                    追加
                  </CmButton>
                </CmBox>
              </CmBox>

              <CmBox mt="20px">
                <CmHeading fontSize="18px" pb="10px">
                  スライドショー設定
                </CmHeading>
              </CmBox>
              <CmBox pl="20px">
                <CmFlex flexWrap="wrap">
                  <DragDropContext onDragEnd={handleOnDragEnd}>
                    <Droppable droppableId="LinkedGroups">
                      {(provided) => (
                        <ul
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          {sites[siteIndex].site_banners.map(
                            (siteBanner, bannerIndex) => {
                              const uniquebannerId = `group-${bannerIndex}`;

                              return (
                                <Draggable
                                  key={bannerIndex}
                                  draggableId={uniquebannerId}
                                  index={bannerIndex}
                                >
                                  {(provided) => (
                                    <li
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                    >
                                      <CmFlex justify="center" mt="30px">
                                        <DragHandleIcon aria-label="ドラッグ" />
                                      </CmFlex>
                                      <CmBox
                                        mr="20px"
                                        mb="10px"
                                        key={`${siteIndex}_${bannerIndex}`}
                                      >
                                        <CmLabelAndUpload
                                          id="logoUrl"
                                          isRequired
                                          fontWeight="bold"
                                          label={`${bannerIndex + 1}枚目`}
                                          src={siteBanner.image_url}
                                          accept={IMAGE_RULE.LOGO.allowedImageTypes.join(
                                            ','
                                          )}
                                          onChange={(e) => {
                                            const file = (
                                              e.target.files as FileList
                                            )[0];
                                            handleChangeBannerImage(
                                              file,
                                              bannerIndex
                                            );
                                          }}
                                          isDisabledDeleteButton={
                                            sites[siteIndex].site_banners
                                              .length === 1
                                          }
                                          onDelete={() =>
                                            handleDeleteBannerImage(bannerIndex)
                                          }
                                        />
                                        <CmLabelAndInput
                                          fontWeight="bold"
                                          label={`${bannerIndex + 1}枚目の${
                                            siteForm.siteBannersLinkUrl.label
                                          }`}
                                          id={`${siteForm.siteBannersLinkUrl.key} + ${siteIndex}_${bannerIndex}`}
                                          value={
                                            sites[siteIndex].site_banners[
                                              bannerIndex
                                            ].link_url || ''
                                          }
                                          onChange={(e) => {
                                            handleChangeBanneerLinkUrl(
                                              e,
                                              siteIndex,
                                              bannerIndex
                                            );
                                          }}
                                          placeholder={
                                            '設定したいURLを入力してください。'
                                          }
                                          onBlur={(e) => {
                                            if (e.target.value === '') {
                                              return;
                                            }
                                            if (
                                              validateLinkUrl(e.target.value)
                                            ) {
                                              alert(
                                                '正しいURLを入力してください。'
                                              );
                                            }
                                          }}
                                        />
                                      </CmBox>
                                    </li>
                                  )}
                                </Draggable>
                              );
                            }
                          )}
                          {provided.placeholder}
                        </ul>
                      )}
                    </Droppable>
                  </DragDropContext>
                </CmFlex>
                <CmFlex alignItems="center">
                  <CmButton
                    leftIcon={<AddIcon />}
                    variant="outline"
                    onClick={handleAddSiteBannerImage}
                  >
                    追加
                  </CmButton>
                  <CmHelpterText ml="5px">
                    ファイルの要件:
                    {IMAGE_RULE.LOGO.allowedImageFormats.join('、')} (最大
                    {unitChangeForByte(IMAGE_RULE.BANNER.maxSize)})
                    <br />
                    最小ピクセル数:横幅は800px以上を推奨
                  </CmHelpterText>
                </CmFlex>
              </CmBox>
              <OrderMethodDisplayNameSettingFormItem
                {...error}
                label={siteForm.orderTypeDisplayName.label}
                custom_order_method_text={
                  sites[siteIndex].custom_order_method_text
                }
                handleChangeIsCustomOrderTypeDisplayName={
                  handleChangeIsCustomOrderTypeDisplayName
                }
                handleChangeOrderTypeDisplayName={
                  handleChangeOrderTypeDisplayName
                }
              />
              <CmBox mt="20px">
                <CmLabelAndTextarea
                  fontWeight="bold"
                  label={siteForm.notificationText.label}
                  id={siteForm.notificationText.key}
                  value={sites[siteIndex].notification_text}
                  onChange={handleInputNotificationText}
                  isInvalid={error[siteForm.notificationText.key].isError}
                  error={
                    <CmFormErrorMessage>
                      {error[siteForm.notificationText.key].message}
                    </CmFormErrorMessage>
                  }
                />
              </CmBox>

              <CmBox mt="20px">
                <CmHeading fontSize="18px" pb="10px" isRequired>
                  注文者からのお店の要望
                </CmHeading>
                <CmBox pl="20px">
                  <CmRadioGroup
                    value={sites[
                      siteIndex
                    ].display_special_instruction.toString()}
                    onChange={handleChangedisplaySpecialInstruction}
                  >
                    <CmStack direction="row">
                      <CmRadio size="md" value={'1'}>
                        お店への要望を受け付ける
                      </CmRadio>
                      <CmRadio size="md" value={'0'}>
                        お店への要望を受け付けない
                      </CmRadio>
                    </CmStack>
                  </CmRadioGroup>
                </CmBox>
              </CmBox>

              <CmBox mt="20px">
                <CmHeading fontSize="18px" pb="10px" isRequired>
                  会員登録・ログイン機能
                </CmHeading>
                <CmBox pl="20px">
                  <CmRadioGroup
                    value={sites[siteIndex].is_auth_available.toString()}
                    onChange={handleChangeIsAuthAvailable}
                  >
                    <CmStack direction="row">
                      <CmRadio size="md" value={'1'}>
                        会員登録・ログイン機能を表示する
                      </CmRadio>
                      <CmRadio size="md" value={'0'}>
                        会員登録・ログイン機能を表示しない
                      </CmRadio>
                    </CmStack>
                  </CmRadioGroup>
                </CmBox>
              </CmBox>

              <CustomFieldForm
                customFields={sites[siteIndex].custom_fields}
                handleRemoveCustomField={handleRemoveCustomField}
                handleChangeCustomField={handleChangeCustomField}
                isOrderMethodChecked={isOrderMethodChecked}
                handleChangeCustomFieldOrderMethod={
                  handleChangeCustomFieldOrderMethod
                }
                handleAddCustomField={handleAddCustomField}
                error={error}
              />
            </CmScrollBox>
          </CmCard>
        ) : (
          <>
            {!isLoading && (
              <CmMessageCard margin="100px auto 0">
                サイト情報が登録されていません。
              </CmMessageCard>
            )}
          </>
        )}
      </div>
      {isLoading && <CmLongLoading />}
    </PageLayout>
  );
};

export default CamelOrderSitesPage;
